import React, { useState, useEffect } from "react";
import { Modal, Button, Alert, Spinner, ProgressBar, Form } from "react-bootstrap";
import { increaseStorage, getStorageState, setStorageImmediate } from "@/api/sharepointApi";

interface Props {
  show: boolean;
  onHide: () => void;
  siteGuid: string;
  siteTitle: string;
  siteUrl: string;
  storageQuotaMb: number;
  storageUsedMb: number;
  storageUsagePercent: number;
  isAdminMode?: boolean;
}

function formatGb(mb: number): string {
  return (mb / 1024).toFixed(1) + " GB";
}

const IncreaseStorageModal: React.FC<Props> = ({
  show,
  onHide,
  siteGuid,
  siteTitle,
  siteUrl,
  storageQuotaMb,
  storageUsedMb,
  storageUsagePercent,
  isAdminMode = false,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [conflictDetected, setConflictDetected] = useState(false);
  const [conflictData, setConflictData] = useState<any>(null);
  const [currentViewedQuota, setCurrentViewedQuota] = useState(storageQuotaMb);
  const [customNewQuotaMb, setCustomNewQuotaMb] = useState<number | null>(null);

  useEffect(() => {
    if (show && isAdminMode) {
      // Fetch current state when modal opens for admin
      getStorageState(siteGuid)
        .then((state) => {
          setCurrentViewedQuota(state.liveStorageQuotaMb);
          // Show warning if database and live values differ
          if (state.needsConfirmation) {
            setWarning(
              `⚠️ Database shows ${(state.databaseStorageQuotaMb / 1024).toFixed(1)} GB, ` +
              `but live SharePoint shows ${(state.liveStorageQuotaMb / 1024).toFixed(1)} GB. ` +
              `Using live value to proceed.`
            );
          }
        })
        .catch(() => {
          // Use existing quota if fetch fails
          setCurrentViewedQuota(storageQuotaMb);
        });
    }
  }, [show, isAdminMode, siteGuid, storageQuotaMb]);

  const handleClose = () => {
    setSuccess(null);
    setError(null);
    setWarning(null);
    setConflictDetected(false);
    setConflictData(null);
    setCustomNewQuotaMb(null);
    onHide();
  };

  const handleIncrease = async (newQuotaMb: number) => {
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      if (isAdminMode) {
        // Admin: queue async update via Function App
        const result = await setStorageImmediate(siteGuid, newQuotaMb, currentViewedQuota);
        // Backend returns 202 Accepted with reportRunGuid
        setSuccess(
          `✓ Storage quota update queued for processing. Job ID: ${result.reportRunGuid}`
        );
        // Close modal after 3 seconds
        setTimeout(() => {
          handleClose();
        }, 3000);
      } else {
        // End user: use async queue
        await increaseStorage(siteGuid, newQuotaMb);
        setSuccess(
          `Your request to set the quota to ${formatGb(newQuotaMb)} has been submitted and will be processed by the operations team.`
        );
      }
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error || err?.message || "Request failed. Please try again.";
      
      if (err?.response?.data?.conflict) {
        // Conflict detected - show conflict modal
        setConflictData({
          currentQuota: err?.response?.data?.currentQuota,
          expectedQuota: currentViewedQuota,
          requestedQuota: newQuotaMb,
          usage: storageUsedMb,
        });
        setConflictDetected(true);
        setCustomNewQuotaMb(newQuotaMb);
      } else {
        setError(errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleConflictOverride = async () => {
    if (!customNewQuotaMb) return;
    setError(null);
    setSuccess(null);
    setConflictDetected(false);
    setSubmitting(true);
    
    try {
      // Fetch fresh state and retry
      const freshState = await getStorageState(siteGuid);
      const result = await setStorageImmediate(siteGuid, customNewQuotaMb, freshState.liveStorageQuotaMb);
      setSuccess(
        `✓ Storage quota update queued for processing. Job ID: ${result.reportRunGuid}`
      );
      setCustomNewQuotaMb(null);
      // Close modal after 3 seconds
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error || err?.message || "Failed to queue storage quota update.";
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Determine tier
  const isLowQuota = storageQuotaMb < 100_000;
  const isHighUsage = storageUsagePercent > 85;
  const isAtMax = !isAdminMode && storageQuotaMb >= 200_000;

  const increaseByQuotaMb = Math.min(storageQuotaMb + 25_600, 204_800);

  const progressVariant =
    storageUsagePercent > 85 ? "danger" : storageUsagePercent > 70 ? "warning" : "success";

  const mailtoBody = encodeURIComponent(
    `Please increase the SharePoint storage quota for the following site:\n\nSite: ${siteTitle}\nURL: ${siteUrl}\nCurrent Quota: ${formatGb(storageQuotaMb)}\nCurrent Usage: ${formatGb(storageUsedMb)} (${storageUsagePercent.toFixed(1)}%)\n\nRequested Action: Increase storage quota.\n\nThank you.`
  );
  const mailtoSubject = encodeURIComponent(`SharePoint Storage Increase Request \u2014 ${siteTitle}`);
  const mailtoHref = `mailto:servicedesk@enmax.com?subject=${mailtoSubject}&body=${mailtoBody}`;

  // Conflict Modal
  if (conflictDetected && conflictData) {
    return (
      <Modal show={true} onHide={() => {}} backdrop="static" keyboard={false}>
        <Modal.Header>
          <Modal.Title>⚠️ Storage Quota Changed</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-3">
            <p className="mb-2">
              The site&apos;s current quota has changed since you opened this dialog.
            </p>
            <ul className="mb-0">
              <li><strong>Current quota:</strong> {formatGb(conflictData.currentQuota)}</li>
              <li><strong>Previous quota (when opened):</strong> {formatGb(conflictData.expectedQuota)}</li>
              <li><strong>Current usage:</strong> {formatGb(conflictData.usage)}</li>
            </ul>
            <p className="mt-2 mb-0 small text-muted">
              This could be from another admin or automatic scaling. Please review and decide how to proceed.
            </p>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setConflictDetected(false);
              setCustomNewQuotaMb(null);
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleConflictOverride}
            disabled={submitting}
          >
            {submitting ? (
              <><Spinner animation="border" size="sm" className="me-1" />Processing…</>
            ) : (
              `Update to ${formatGb(conflictData.requestedQuota)} anyway`
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Manage Storage</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
        {warning && <Alert variant="warning" onClose={() => setWarning(null)} dismissible>{warning}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <div className="mb-3">
          <div className="fw-semibold mb-1">{siteTitle}</div>
          <div className="text-muted small mb-2">
            {formatGb(storageUsedMb)} used of {formatGb(storageQuotaMb)} quota
          </div>
          <ProgressBar
            now={Math.min(storageUsagePercent, 100)}
            variant={progressVariant}
            label={`${storageUsagePercent.toFixed(1)}%`}
          />
        </div>

        {!success && (
          <>
            {isAdminMode ? (
              // Admin mode: allow custom quota input
              <>
                <p className="mb-3">As an admin, you can set any quota value.</p>
                <Form.Group className="mb-3">
                  <Form.Label>New Quota (in MB)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="e.g. 250000"
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      if (!isNaN(val)) setCustomNewQuotaMb(val);
                    }}
                  />
                  <Form.Text className="text-muted">
                    Current quota: {formatGb(currentViewedQuota)}
                  </Form.Text>
                </Form.Group>
                <Button
                  variant="primary"
                  onClick={() => customNewQuotaMb && handleIncrease(customNewQuotaMb)}
                  disabled={submitting || !customNewQuotaMb}
                >
                  {submitting ? (
                    <><Spinner animation="border" size="sm" className="me-1" />Processing…</>
                  ) : (
                    `Update to ${formatGb(customNewQuotaMb || 0)}`
                  )}
                </Button>
              </>
            ) : isAtMax ? (
              <Alert variant="info">
                <p className="mb-2">
                  This site already has a quota of <strong>{formatGb(storageQuotaMb)}</strong>.
                  Increasing beyond 200 GB requires a service request.
                </p>
                <Button
                  as="a"
                  href={mailtoHref}
                  variant="primary"
                  size="sm"
                >
                  Submit Service Request
                </Button>
              </Alert>
            ) : isLowQuota ? (
              <>
                <p>This site&apos;s quota is below 100 GB. You can increase it to a standard 100 GB allocation.</p>
                <Button
                  variant="primary"
                  onClick={() => handleIncrease(102_400)}
                  disabled={submitting}
                >
                  {submitting ? (
                    <><Spinner animation="border" size="sm" className="me-1" />Processing…</>
                  ) : (
                    `Set quota to 100 GB (102,400 MB)`
                  )}
                </Button>
              </>
            ) : isHighUsage ? (
              <>
                <p>
                  Storage usage is above 85%. You can increase the quota by 25 GB
                  (to {formatGb(increaseByQuotaMb)}).
                </p>
                <Button
                  variant="primary"
                  onClick={() => handleIncrease(increaseByQuotaMb)}
                  disabled={submitting}
                >
                  {submitting ? (
                    <><Spinner animation="border" size="sm" className="me-1" />Processing…</>
                  ) : (
                    `Increase by 25 GB (new quota: ${formatGb(increaseByQuotaMb)})`
                  )}
                </Button>
              </>
            ) : (
              <Alert variant="secondary">
                Storage usage is within normal range. No increase is needed at this time.
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {success ? "Close" : "Cancel"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default IncreaseStorageModal;
